/**
 * OLI Gateway - Document Client
 *
 * Client for document management via oli-gateway.
 * Endpoints: /v1/documents/*
 */

import { config } from "../config";
import {
  Documento,
  DocumentoLookupResponse,
  DocumentoStatus,
  BindDocumentoRequest,
} from "../types";

// Gateway response envelope
interface GatewayResponse<T> {
  meta: {
    request_id: string;
    timestamp: string;
    version: string;
  };
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

class OliDocClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.gateway.baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Use proxy in development to avoid CORS
    const isDev = window.location.hostname === "localhost";
    // endpoint comes as "/v1/xxx" — proxy rewrites /api → /v1
    const url = isDev ? `/api${endpoint.replace(/^\/v1/, "")}` : `${this.baseUrl}${endpoint}`;
    console.log(`[OliDocClient] ${options.method || "GET"} ${url}${isDev ? " (via proxy)" : ""}`);

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    console.log(`[OliDocClient] Response: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      console.error(`[OliDocClient] Error:`, body);
      const errorMsg = body.error?.message || body.detail || `HTTP ${response.status}`;
      throw new OliDocError(errorMsg, response.status);
    }

    // Handle 204 No Content responses
    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return undefined as T;
    }

    const body = await response.json();

    // Unwrap gateway envelope if present
    if (body && typeof body === "object" && "success" in body && "data" in body) {
      if (!body.success) {
        throw new OliDocError(
          body.error?.message || "Gateway returned success=false",
          response.status
        );
      }
      return body.data as T;
    }

    // Direct response (no envelope)
    return body as T;
  }

  /**
   * Get document by ID (meta_document_id)
   */
  async getDocumentoById(documentoId: string): Promise<Documento | null> {
    try {
      return await this.request<Documento>(`/v1/documents/${documentoId}`);
    } catch (error) {
      if (error instanceof OliDocError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get document by SharePoint item ID (fallback lookup)
   * Uses GET /v1/documents?item_id=xxx and converts to DocumentoLookupResponse
   */
  async getDocumentoByItemId(itemId: string): Promise<DocumentoLookupResponse> {
    try {
      const docs = await this.request<Documento[]>(
        `/v1/documents?item_id=${encodeURIComponent(itemId)}`
      );
      if (docs && docs.length > 0) {
        return { bound: true, documento: docs[0], message: null };
      }
      return { bound: false, documento: null, message: "Documento não encontrado por item_id" };
    } catch (error) {
      if (error instanceof OliDocError && error.status === 404) {
        return { bound: false, documento: null, message: "Documento não encontrado" };
      }
      throw error;
    }
  }

  /**
   * Bind an external document to a process
   */
  async bindDocumento(data: BindDocumentoRequest): Promise<Documento> {
    return this.request<Documento>("/v1/documents/bind", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * Update document status
   */
  async updateStatus(
    documentoId: string,
    status: DocumentoStatus
  ): Promise<Documento> {
    return this.request<Documento>(`/v1/documents/${documentoId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  }

  /**
   * Checkout document for editing
   */
  async checkout(documentoId: string, userId: string, timeoutMinutes = 120): Promise<void> {
    await this.request<void>(`/v1/documents/${documentoId}/checkout`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, timeout_minutes: timeoutMinutes }),
    });
  }

  /**
   * Checkin document after editing
   */
  async checkin(documentoId: string, userId: string, comment?: string): Promise<void> {
    await this.request<void>(`/v1/documents/${documentoId}/checkin`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, ...(comment && { comment }) }),
    });
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const url = `${this.baseUrl}/health`;
      const response = await fetch(url);
      return response.ok;
    } catch {
      return false;
    }
  }
}

/**
 * Custom error class for OLI Document API errors
 */
export class OliDocError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = "OliDocError";
  }
}

// Singleton instance
export const oliDocClient = new OliDocClient();
